import { Module, ModuleCollection } from '@msdk/common'

import { classNameFromFullname } from './classname-from-fullname'

/**
 * The function `resolveItem` returns the imported component based on the provided module and full
 * name.
 * @param {Module} module - The `module` parameter is an object that represents a module
 * containing exported components or classes.
 * @param {string} fullName - The `fullName` parameter is a string that represents the full name of the
 * item you want to resolve. It is used to determine the class name of the item.
 * @returns the resolved item
 */
export function resolveItem(module: Module, fullName: string | undefined): new (...args: any[]) => any | JSX.Element {
  if (fullName && module) {
    const windowObj = window as any
    const FunctionalComponents = windowObj.FunctionalComponents
    const className = classNameFromFullname(fullName)
    const ImportedComponent = fullName.includes('@msdk/functional-components')
      ? (FunctionalComponents as any)[className]
      : module[className]
    return ImportedComponent
  }
  return null as any
}

/**
 * The function `resolveItemFromAll` takes in a collection of modules and a full name, and returns the
 * resolved component from one of the loaded modules that matches the full name.
 * @param {ModuleCollection} modules - The `modules` parameter is of type `ModuleCollection`, which is
 * an object containing loaded modules. It likely has a structure like this:
 * @param {string | undefined} fullName - The `fullName` parameter is a string that represents the full
 * name of the item you want to resolve. It is optional and can be undefined.
 * @returns a constructor function or JSX element.
 */
export function resolveItemFromAll(
  modules: ModuleCollection,
  fullName: string | undefined,
): new (...args: any[]) => any | JSX.Element {
  let resolvedComponent: any = null
  if (fullName) {
    const loadedMs = Object.keys(modules.loadedModules)
    for (let i = 0; i < loadedMs.length; i++) {
      const ms = loadedMs[i]
      const module = modules.loadedModules[ms]
      resolvedComponent = resolveItem(module, fullName)
      if (resolvedComponent) break
    }
  }
  return resolvedComponent
}
